<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useUser } from '@/stores/user'
import { userGroups } from '~/constants/userGroups'
import type { RouteNamedMapI18n } from 'vue-router/auto-routes'

const { t } = useI18n()
const { getStaticPageSlug } = useStaticPages()

const userStore = useUser()

const { user, isLoggedIn, userGroupId } = storeToRefs(userStore)

const localePath = useLocalePath()
const { locale } = useI18n()

const links = computed<
  {
    name: keyof RouteNamedMapI18n
    slug: string | undefined
    labelPath: string
  }[]
>(() => [
  { name: 'news', slug: undefined, labelPath: 'pages.news.label' },
  { name: 'references', slug: undefined, labelPath: 'pages.references.label' },
  { name: 'designers', slug: undefined, labelPath: 'pages.designers.label' },
  { name: 'download', slug: undefined, labelPath: 'pages.toDownload.label' },
  { name: 'about', slug: undefined, labelPath: 'pages.about.label' },
  {
    name: 'slug',
    slug: getStaticPageSlug(staticPageKeys.CONTACT),
    labelPath: 'pages.contact.label',
  },
])

const changingLang = ref(false)
const userDetailOpen = ref(false)
const priceSettingsOpen = ref(false)
const searching = ref(false)

const alternativeMenuRef = ref<HTMLElement | null>(null)

onClickOutside(alternativeMenuRef, () => {
  if (
    (changingLang.value || userDetailOpen.value || priceSettingsOpen.value) &&
    !isOpen.value
  ) {
    changingLang.value = false
    userDetailOpen.value = false
    priceSettingsOpen.value = false
  }
})

watch(
  [changingLang, userDetailOpen, priceSettingsOpen, searching],
  (
    [changingLangNew, userDetailOpenNew, priceSettingsOpenNew, searchingNew],
    [changingLangOld, userDetailOpenOld, priceSettingsOpenOld, searchingOld]
  ) => {
    if (
      searchingOld &&
      (changingLangNew || userDetailOpenNew || priceSettingsOpenNew)
    ) {
      searching.value = false
    }

    if (
      (changingLangOld || userDetailOpenOld || priceSettingsOpenOld) &&
      searchingNew
    ) {
      changingLang.value = false
      userDetailOpen.value = false
      priceSettingsOpen.value = false
    }
  }
)

const isOpen = ref(false)

const { proxy } = useSmartsupp()

watch(
  () => isOpen.value,
  (value) => {
    if (value) {
      proxy.smartsupp('chat:hide')
    } else {
      proxy.smartsupp('chat:show')
    }
  },
  { immediate: true }
)

watch(isLoggedIn, (value) => {
  if (!value && userDetailOpen.value) {
    toggleUserDetail()
  }
})

function toggleLangSelector() {
  userDetailOpen.value = false
  priceSettingsOpen.value = false
  changingLang.value = !changingLang.value
}

function toggleUserDetail() {
  changingLang.value = false
  priceSettingsOpen.value = false
  userDetailOpen.value = !userDetailOpen.value
}

function togglePriceSettings() {
  changingLang.value = false
  userDetailOpen.value = false
  priceSettingsOpen.value = !priceSettingsOpen.value
}

const { currentRoute } = useRouter()
watch(
  () => currentRoute.value.fullPath,
  () => {
    isOpen.value = false
    changingLang.value = false
    userDetailOpen.value = false
    priceSettingsOpen.value = false
    searching.value = false
  }
)

const { width } = useWindowSize()

watch(width, () => {
  if (isOpen.value && width.value > 1024) {
    isOpen.value = false
  }
})

const scrollY = ref(0)

watch(
  [isOpen, searching, changingLang],
  ([_, searchingNew, changingLangNew], [__, searchingOld, changingLangOld]) => {
    if (searching.value && isOpen.value) {
      isOpen.value = false
    }

    if (searchingOld && changingLangNew) {
      searching.value = false
    }

    if (changingLangOld && searchingNew) {
      changingLang.value = false
    }

    if (isOpen.value || searching.value) {
      scrollY.value = window.scrollY
      document.getElementsByTagName('html')[0]?.classList.add('overflow-hidden')
    } else {
      document
        .getElementsByTagName('html')[0]
        ?.classList.remove('overflow-hidden')
    }
  }
)

const repository = createProductSmartFiltersRepository()

const { getResourceUrl } = useResources()

const showProducts = ref(false)
const chosenItem = ref<'featured' | 'ourProducts'>('featured')

const smartFiltersDataFn = () =>
  useLazyAsyncData(
    'featuredSmartFilters_' + locale.value,
    () => repository.getFeaturedFilters(),
    {
      watch: [locale],
    }
  )

// TODO: Fix after this issue is resolved: https://github.com/nuxt/nuxt/issues/33680
const { data: smartFiltersData } = import.meta.server
  ? await smartFiltersDataFn()
  : smartFiltersDataFn()

const smartFilters = computed(() => smartFiltersData.value?.items || [])

watch(
  smartFilters,
  () => {
    if (!smartFilters.value.length) {
      chosenItem.value = 'ourProducts'
    }
  },
  {
    immediate: true,
  }
)

// const productLinesRepository = createProductLineRepository()

// const { data: topSeriesData } = await useLazyAsyncData('topSeries', () => {
//   return Promise.all(
//     topSeries.map((series) => {
//       return productLinesRepository.getLineBySlug(series.slug)
//     })
//   )
// })

const followUsLinks = computed(() => [
  {
    link: 'https://www.facebook.com/LDSeating',
    label: 'Facebook',
    imageSrc: '/assets/front/images/icons/social/facebook.svg',
  },
  {
    link: 'https://www.instagram.com/ldseating/',
    label: 'Instagram',
    imageSrc: '/assets/front/images/icons/social/instagram.svg',
  },
  {
    link: 'https://cz.pinterest.com/ldseating/',
    label: 'Pinterest',
    imageSrc: '/assets/front/images/icons/social/pinterest.svg',
  },
  {
    link: 'https://www.linkedin.com/company/ldseating',
    label: 'LinkedIn',
    imageSrc: '/assets/front/images/icons/social/linkedin.svg',
  },
])
</script>

<template>
  <header class="relative" role="banner">
    <div
      v-if="searching"
      class="absolute left-0 top-full z-50 w-full bg-white lg:hidden"
    >
      <div class="container">
        <UiHeaderFulltextSearch @close="searching = false" />
      </div>
    </div>
    <div class="container">
      <div class="flex h-full max-w-full flex-row items-center">
        <div class="flex flex-1 flex-row items-center">
          <div
            class="flex h-24 w-full items-center justify-between lg:h-40"
            :class="{ 'lg:w-max': searching }"
          >
            <NuxtLink
              aria-label="Homepage link"
              :to="localePath({ name: 'index' })"
            >
              <UiLDSLogo class="h-16 lg:h-24" />
            </NuxtLink>

            <div class="flex gap-5 lg:hidden xl:gap-4">
              <UiHeaderFulltextSearchBtn
                v-model="searching"
                is-mobile
                class="lg:hidden"
              />
              <button
                v-if="!searching"
                class="flex items-center lg:hidden"
                aria-label="Menu button"
                @click="isOpen = !isOpen"
              >
                <IconHamburger v-if="!isOpen" />
                <IconClose v-else-if="!searching" />
              </button>
            </div>
          </div>
          <div v-if="searching" class="flex-1 px-20 max-lg:hidden">
            <UiHeaderFulltextSearch @close="searching = false" />
          </div>
        </div>
        <div
          ref="alternativeMenuRef"
          class="grid h-full grid-cols-1 grid-rows-2 max-lg:hidden xl:ml-24 2xl:ml-64"
        >
          <div class="mb-4 flex items-end justify-end gap-8">
            <UiHeaderFulltextSearchBtn v-model="searching" />
            <LazyLoginButton v-if="!isLoggedIn" />
            <template v-else>
              <UiHeaderExpandableButton
                :label="(user?.firstName ?? '') + ' ' + (user?.lastName ?? '')"
                :active="userDetailOpen"
                @toggle="toggleUserDetail"
              />
              <UiHeaderExpandableButton
                v-if="userGroupId === userGroups.ADMIN"
                :label="t('base.header.priceSettings.title')"
                :active="priceSettingsOpen"
                @toggle="togglePriceSettings"
              />
            </template>
            <UiHeaderExpandableButton
              :label="locale"
              :active="changingLang"
              uppercase
              @toggle="toggleLangSelector"
            />
          </div>
          <div
            v-if="!searching"
            class="mt-4 flex flex-row items-baseline justify-end gap-3 xl:gap-6 xl:text-lg"
          >
            <template v-if="userDetailOpen">
              <LazyUiHeaderUserMenu />
            </template>
            <template v-else-if="priceSettingsOpen">
              <LazyUiHeaderPriceSettings />
            </template>
            <template v-else-if="changingLang">
              <LazyUiHeaderLangSelector />
            </template>
            <template v-else>
              <LazyUiProductLinesDropdown />
              <LazyUiHeaderLinkButton
                v-for="(link, index) in links"
                :key="index"
                :link="link"
              />
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="isOpen"
        class="absolute left-0 z-50 h-[calc(100svh-6rem)] w-full overflow-x-auto bg-white lg:hidden"
      >
        <div class="container flex h-full flex-col justify-between pb-5">
          <div>
            <div
              v-if="!showProducts"
              class="mb-8 flex flex-col items-end gap-5 text-right"
            >
              <div class="group font-bold transition duration-300 xl:text-lg">
                <button @click="showProducts = true">
                  <div class="flex items-center">
                    <span class="whitespace-nowrap">
                      {{ t('base.products.products') }}
                    </span>
                    <IconDropdownArrow class="ml-1 -rotate-90" />
                  </div>
                </button>
                <span
                  class="-mt-1 block h-[1px] max-w-0 bg-black transition-all duration-500 group-hover:max-w-full"
                  :class="{
                    '!max-w-full': $route.path.includes(
                      localePath({ name: 'products' })
                    ),
                  }"
                />
              </div>
              <LazyUiHeaderLinkButton
                v-for="(link, index) in links"
                :key="index"
                :link="link"
              />
            </div>
            <div v-else class="mb-8 flex flex-col items-end gap-5 text-right">
              <div class="flex w-full justify-between">
                <button @click="showProducts = false">
                  <IconDropdownArrow class="ml-1 rotate-90" />
                </button>
                <div class="flex gap-2 text-lg font-bold">
                  <button
                    :class="{ underline: chosenItem === 'ourProducts' }"
                    @click="chosenItem = 'ourProducts'"
                  >
                    {{ $t('base.header.ourProducts') }}
                  </button>
                </div>
              </div>
              <LazyHeaderCategories v-if="chosenItem === 'ourProducts'" />
            </div>
            <div class="pb-4">
              <div class="mb-4 flex w-full items-baseline justify-between">
                <LazyLoginButton v-show="!isLoggedIn" />
                <template v-if="isLoggedIn">
                  <UiHeaderExpandableButton
                    :label="
                      (user?.firstName ?? '') + ' ' + (user?.lastName ?? '')
                    "
                    :active="userDetailOpen"
                    @toggle="toggleUserDetail"
                  />
                  <UiHeaderExpandableButton
                    v-if="userGroupId === userGroups.ADMIN"
                    :label="t('base.header.priceSettings.title')"
                    :active="priceSettingsOpen"
                    @toggle="togglePriceSettings"
                  />
                </template>
                <div class="flex gap-2">
                  <LazyUiHeaderLangSelector />
                </div>
              </div>
              <div v-if="userDetailOpen">
                <LazyUiHeaderUserMenu />
              </div>
              <div v-else-if="priceSettingsOpen">
                <LazyUiHeaderPriceSettings />
              </div>
            </div>
            <div class="flex flex-col gap-5">
              <NuxtLink
                :to="localePath({ name: 'products' })"
                class="group/products font-bold no-underline transition duration-300"
                @click="isOpen = false"
              >
                <UiButtonBlack class="w-full">
                  {{ $t('base.products.productsLink') }}
                </UiButtonBlack>
              </NuxtLink>
              <NuxtLink
                :to="localePath({ name: 'product-lines' })"
                class="group/products font-bold no-underline transition duration-300"
                @click="isOpen = false"
              >
                <UiButtonBlackOutline class="w-full">
                  {{ $t('base.header.seriesLink') }}
                </UiButtonBlackOutline>
              </NuxtLink>
            </div>
          </div>
          <div class="flex justify-between gap-5 py-10">
            <TypographyBaseBold>
              {{ t('base.header.followUs') }}
            </TypographyBaseBold>
            <div class="flex gap-2">
              <NuxtLink
                v-for="(link, index) in followUsLinks"
                :key="'follow-us-link-' + index"
                :to="link.link"
                target="_blank"
                class="d-inline-block align-middle no-underline"
              >
                <img
                  :src="getResourceUrl(link.imageSrc)"
                  height="24"
                  class="h-6"
                  alt="Social media icon"
                  loading="lazy"
                />
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>
